import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Routes } from '../../config/routes';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { map } from 'rxjs/operators';
import { Transaction } from '../../models/entities/transactions/transaction';
import { PaginationLinks, PaginationMeta } from '../../models/entities/pagination';
import { TransactionRule } from '../../models/entities/transactions/transaction-rules/transaction-rule';
import { TransactionDatasourceOptions } from './transactions.datasource';
import { Category } from '../../models/entities/accounting/category';
import { UnifiedBankAccount } from '../../models/entities/unified-bank-account';
import moment from 'moment';
import { Accounting, CreateJournalRequest } from 'app/components/shared/http/request-interfaces';

export class SplitRequest
{
    originalTransaction: number;
    splits: { amount: number, categoryId: number, description: string, vendorId: number }[];

    constructor(data: any) {
        this.originalTransaction = data.originalTransaction;
        this.splits = data.splits;
    }
}

export interface CreateTransactionRule
{
    name: string;
    type: string;
    transactionId?: number;
    account?: number;
    autoApprove: boolean;
    matchAll: boolean;
    accountType: string;
    accountId: number;
    vendorId?: number;
    comparisons: {
        field: string,
        comparison: string,
        value: string
    }[];
}

export class PatchTransactionRequest
{
    vendorId: number;
    bankAccountId: number;
    ownerId: number;
    ownerType: string;
    categoryId: number;
    amount: number;
    description: string;
    transactionDate: string;
    unitId: number;
    approved: boolean;
    excluded: boolean;
    billPaymentId: number;
    isExternalRevenue: boolean;
    memo: string;
    allianceTransactionId: number;
    convertToTransaction: boolean;

    constructor(body: any, tx: Transaction) {
        if (body.hasOwnProperty('vendorId') && body.vendorId !== tx.vendorId) {
            this.vendorId = typeof body.vendorId === 'undefined' ? null : body.vendorId;
            if (!body.vendorId) {
                this.billPaymentId = null;
            }
        }
        if (body.hasOwnProperty('isExternalRevenue') && body.isExternalRevenue !== tx.isExternalRevenue) {
            this.isExternalRevenue = body.isExternalRevenue;
        }

        if (body.hasOwnProperty('billPaymentId') && body.billPaymentId !== tx.billPaymentId) {
            this.billPaymentId = body.billPaymentId;
        }

        if (body.hasOwnProperty('bankAccountId') && body.bankAccountId !== tx.bankAccountId) {
            this.ownerId = body.bankAccountId;
            this.ownerType = 'bank';
        }

        if ((body.accountId && !body.bankAccountId) && body.accountId !== tx.bankAccountId) {
            this.ownerId = body.accountId;
            this.ownerType = 'bank';
        }

        if (body.ownerId && body.ownerId !== tx.ownerId) {
            this.ownerId = body.ownerId;
        }

        if (body.ownerType && body.ownerType !== tx.ownerType) {
            this.ownerType = body.ownerType;
        }

        if (body.amount && body.amount !== tx.amount) {
            this.amount = body.amount;
        }

        if (body.description && body.description !== tx.description) {
            this.description = body.description;
        }

        if (body.transactionDate && body.transactionDate !== tx.transactionDate) {
            if (moment.isMoment(body.transactionDate)) {
                body.transactionDate = body.transactionDate.format('YYYY-MM-DD');
            }
            this.transactionDate = body.transactionDate;
        }

        if (body.unitId && body.unitId !== tx.unitId) {
            this.unitId = body.unitId;
        }

        if (body.hasOwnProperty('excluded') && body.excluded !== tx.excluded) {
            this.excluded = body.excluded;
        }

        if (body.hasOwnProperty('approved') && body.approved !== tx.approved) {
            this.approved = body.approved;
        }

        if (body.category && body.category.id !== tx.categoryId) {
            this.categoryId = body.category.id;
            this.approved = true;
        }

        if (tx.category && !body.category) {
            this.categoryId = null;
            this.approved = false;
        }

        if (body.memo && body.memo !== tx.memo) {
            this.memo = body.memo;
        }
        if (body.allianceTransactionId && body.allianceTransactionId !== tx.allianceTransactionId) {
            this.allianceTransactionId = body.allianceTransactionId;
        }

        this.convertToTransaction = tx.convertToTransaction;
    }
}

export class RecordNewTransferRequest
{
    transactionDate: string;
    amount: number;
    description: string;
    toId: number;
    toType: string;
    fromId: number;
    fromType: string;
    memo: string;

    constructor(body) {
        this.transactionDate = body.transactionDate;
        this.amount = body.amount;
        this.description = body.description;

        this.toId = body.to.id;
        this.toType = body.to.accountType;
        this.fromId = body.from.id;
        this.fromType = body.from.accountType;

        if (body.memo) {
            this.memo = body.memo;
        }
    }
}

export class RecordTransferRequest
{
    sourceId: number;
    sourceType: string;
    destinationId: number;
    destinationType: string;
    description: string;
    transactionDate: string;
    amount: number;
    memo: string;

    constructor(body: any) {
        this.sourceId = body.sourceId;
        this.sourceType = body.sourceType;
        this.destinationId = body.destinationId;
        this.destinationType = body.destinationType;
        this.description = body.description;
        this.transactionDate = body.transactionDate;
        this.amount = body.amount;
        this.memo = body.memo ?? null;
    }
}

export class TransactionsResponse
{
    data: Transaction[];
    links: PaginationLinks;
    meta: PaginationMeta;

    constructor(request: any) {
        this.meta = new PaginationMeta(request.meta);
        this.data = (request.data && request.data.length) ? request.data.map(tx => {
            const transaction = new Transaction(tx);
            if (this.meta.included) {
                transaction.category = this.meta.included.categories[tx.categoryId]
                        ? new Category(this.meta.included.categories[tx.categoryId])
                        : null;
                for (const child of transaction.children) {
                    child.category = this.meta.included.categories[child.categoryId]
                            ? new Category(this.meta.included.categories[child.categoryId])
                            : null;
                    for (const child2 of child.children) {
                        child2.category = this.meta.included.categories[child2.categoryId]
                                ? new Category(this.meta.included.categories[child2.categoryId])
                                : null;
                    }
                    child.updateDepositCategory();
                }
                transaction.updateDepositCategory();
                transaction.bankAccount = this.meta.included.owners[tx.ownerId]
                        ? new UnifiedBankAccount(this.meta.included.owners[tx.ownerId])
                        : null;
            }
            return transaction;
        }) : [];
        this.links = new PaginationLinks(request.links);
    }
}

@Injectable({
    providedIn: 'root',
})
export class TransactionsService
{
    private _http: ApplicationHttpClient;

    constructor(_http: ApplicationHttpClient) {
        this._http = _http;
    }

    /**
     * For fixing failed payouts being retried later but not tracked
     * @param transactionId
     * @param paymentAttemptId
     */
    public associatePendingFundWithDeposit(transactionId, paymentAttemptId) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.AssociatePendingFunds(jwt.orgId, transactionId, paymentAttemptId),
        );

        return this._http.post(url, JSON.stringify({}));
    }

    /**
     * Get a count of uncategorized transactions for the specified organization.
     * @param {number} organizationId
     * @returns {any}
     */
    public getUncategorizedCount(organizationId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Transactions(organizationId || jwt.orgId) + '/count?filter=uncategorized',
        );

        return this._http.get(url);
    }

    /**
     * On click to view detail of any Tx with a parentId, fetch the info re parent and children for detail display.
     * @param transactionId
     */
    public getSplitTransactionDetails(transactionId) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Transaction(jwt.orgId, transactionId) + '/splits',
        );

        return this._http.get(url);
    }

    /**
     * Gets paginated collection of transactions for the specified organization.
     * @returns {any}
     * @param orgId
     * @param search
     * @param filter
     * @param sortColumn
     * @param sortDirection
     * @param pageIndex
     * @param perPage
     * @param options
     */
    public getTransactions(
            orgId: number,
            search: string = '',
            filter: string = '',
            sortColumn: string = 'transactionDate',
            sortDirection: string = 'desc',
            pageIndex: number = 1,
            perPage: number = 100,
            options: TransactionDatasourceOptions = {},
    ): Observable<TransactionsResponse> {

        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        let url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Transactions(orgId || jwt.orgId),
        );
        const params = [];
        params.push('page=' + pageIndex);
        params.push('search=' + encodeURIComponent(search));
        params.push('filter=' + filter);
        params.push('column=' + sortColumn);
        params.push('direction=' + sortDirection);
        params.push('perPage=' + perPage);
        if (typeof options !== 'undefined') {
            // find any moments in the options and convert them to strings
            options = Object.keys(options).reduce((acc, key) => {
                if (moment.isMoment(options[key])) {
                    acc[key] = options[key].format('YYYY-MM-DD');
                } else {
                    acc[key] = options[key];
                }
                return acc;
            }, {});

            params.push('filters=' + JSON.stringify(options));
        }

        url = url + '?' + params.join('&');

        return this._http.get(url)
                .pipe(
                        map(
                                (resp) => {
                                    return new TransactionsResponse(resp);
                                },
                        ),
                );
    }

    public getTransactionsCount(orgId: number): Observable<number> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Transactions(orgId || jwt.orgId) + '/count',
        );

        return this._http.get(url)
                .pipe(
                        map(
                                (resp) => {
                                    return resp['count'];
                                },
                        ),
                );
    }

    /**
     * Update a transaction record for the specified organization.
     * @param organizationId
     * @param transactionId
     * @param requestBody
     * @returns {any}
     */
    public updateTransaction(
            organizationId: number,
            transactionId: number,
            requestBody: PatchTransactionRequest,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.patch(
                Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.Transaction(organizationId || jwt.orgId, transactionId),
                ),
                JSON.stringify(requestBody),
        );
    }

    /**
     * @param {number} organizationId
     * @param {any} requestBody
     * @returns {any}
     */
    public bulkImportTransactions(organizationId: number, requestBody: any): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.TransactionsBulkImport(organizationId || jwt.orgId),
        );

        return this._http.post(url, JSON.stringify(requestBody));
    }

    public splitTransactions(organizationId: number, body: SplitRequest): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Transactions(organizationId || jwt.orgId) + '/split',
        );

        return this._http.post(url, JSON.stringify(body));
    }

    /**
     * Delete a collection of transaction records from the specified organization.
     * @param {number} organizationId
     * @param {number[]} transactionIds
     * @returns {any}
     */
    public deleteTransactions(organizationId: number, transactionIds: number[]): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.DeleteTransactions(organizationId || jwt.orgId),
        );

        return this._http.patch(url, JSON.stringify({transactions: transactionIds}));
    }

    /**
     * Restore a collection of transaction records from the specified organization.
     * @param {number} organizationId
     * @param {number[]} transactionIds
     * @returns {any}
     */
    public restoreTransactions(organizationId: number, transactionIds: number[]): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.RestoreTransactions(organizationId || jwt.orgId),
        );

        return this._http.patch(url, JSON.stringify({transactions: transactionIds}));
    }

    /**
     * Merge multiple transaction records into a single transaction record for the specified organization.
     * @param {number} organizationId
     * @param {any} requestBody
     * @returns {any}
     */
    public mergeTransactions(organizationId: number, requestBody: any): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.TransactionsMerge(organizationId || jwt.orgId),
        );

        return this._http.post(url, JSON.stringify(requestBody));
    }

    /**
     * Update category relation for a collection of transactions for the specified organization.
     * @param {number} organizationId
     * @param {number} categoryId
     * @param {any} requestBody
     * @returns {any}
     */
    public updateCategoriesForTransactions(
            organizationId: number,
            categoryId: number,
            requestBody: any,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.TransactionsCategory(organizationId || jwt.orgId, categoryId),
        );

        return this._http.put(url, JSON.stringify(requestBody));
    }

    /**
     * Create a new transaction record for the specified organization.
     * @param {number} organizationId
     * @param {any} requestBody
     * @returns {any}
     */
    public createTransaction(
            organizationId: number,
            requestBody: Accounting.CreateTransactionRequest,
    ): Observable<Transaction> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.Transactions(organizationId || jwt.orgId));

        return this._http.post(url, JSON.stringify(requestBody))
                .pipe(map(item => new Transaction(item['data'])));
    }

    /**
     * Get transaction totals by category for the specified organization.
     * @param {number} organizationId
     * @returns {any}
     */
    public getCategoryTotals(organizationId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.get(
                Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.TransactionCategoryTotals(organizationId || jwt.orgId),
                ),
        );
    }

    /**
     * Get transaction filters for org.
     * @param {number} organizationId
     * @returns {Observable<Object>}
     */
    public getTransactionFilters(organizationId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.TransactionFilters(organizationId || jwt.orgId),
        );

        return this._http.get(url);
    }

    public deleteAttachment(transactionId: number, attachmentId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.TransactionAttachments(jwt.orgId, transactionId) + '/' + attachmentId,
        );

        return this._http.delete(url);
    }

    public createJournalEntry(orgId: number, body: CreateJournalRequest): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Transactions(jwt.orgId) + '/journal-entry',
        );

        return this._http.post(url, JSON.stringify(body));
    }

    public updateJournalEntry(orgId: number, transactionId, body: any) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Transactions(jwt.orgId) + '/journal-entry/' + transactionId,
        );

        return this._http.put(url, JSON.stringify(body));
    }

    public getTransactionRules(orgId: number): Observable<TransactionRule[]> {
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Transactions(orgId) + '/rules',
        );

        return this._http.get(url).pipe(
                map((items: Object[]) => {
                    return items.map(r => {
                        return new TransactionRule(r);
                    });
                }));
    }

    public getRule(orgId: number, ruleId: number): Observable<TransactionRule> {
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Transactions(orgId) + '/rules/' + ruleId,
        );

        return this._http.get(url)
                .pipe(
                        map(
                                (item) => {
                                    return new TransactionRule(item);
                                },
                        ),
                );
    }

    public createTransactionRule(orgId: number, body: CreateTransactionRule): Observable<TransactionRule> {
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Transactions(orgId) + '/rules',
        );

        return this._http.post(url, JSON.stringify(body))
                .pipe(
                        map(
                                (item) => {
                                    return new TransactionRule(item);
                                },
                        ),
                );
    }

    public putTransactionRule(orgId: number, ruleId: number, body: CreateTransactionRule): Observable<TransactionRule> {
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Transactions(orgId) + '/rules/' + ruleId,
        );

        return this._http.put(url, JSON.stringify(body))
                .pipe(
                        map(
                                (item) => {
                                    return new TransactionRule(item);
                                },
                        ),
                );
    }

    public deleteTransactionRule(orgId: number, ruleId: number): Observable<any> {
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Transactions(orgId) + '/rules/' + ruleId,
        );

        return this._http.delete(url);
    }

    public recordTransferFromTransaction(
            orgId: number,
            txId: number,
            body: RecordTransferRequest,
    ): Observable<Transaction> {
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Transaction(orgId, txId) + '/transfer',
        );

        return this._http.post(url, JSON.stringify(body))
                .pipe(
                        map(
                                (item) => {
                                    return new Transaction(item['data']);
                                },
                        ),
                );
    }

    public recordTransfer(orgId: number, body: RecordNewTransferRequest): Observable<Transaction> {
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Transactions(orgId) + '/transfer',
        );

        return this._http.post(url, JSON.stringify(body))
                .pipe(
                        map(
                                (item) => {
                                    return new Transaction(item['data']);
                                },
                        ),
                );
    }

    public replaceBankAccountForOfflinePayment(
            transactionId: number,
            newAccount: number,
            replaceAll: boolean,
    ): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Transaction(jwt.orgId, transactionId) + '/replace-deposit-account/' + newAccount + (replaceAll
                        ? '/1'
                        : '/0'),
        );

        return this._http.post(url, JSON.stringify({}));
    }
}
